import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore, useUIStore, useNotificationStore } from '../store';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { NotificationsDropdown } from './NotificationsDropdown';
import { playPing } from '../lib/ping';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import {
  LayoutDashboard,
  DollarSign,
  Megaphone,
  TrendingUp,
  Settings2,
  Target,
  Users,
  BookOpen,
  Heart,
  GraduationCap,
  CheckSquare,
  UserCircle,
  Bell,
  Calendar,
  MessageSquare,
  FileText,
  FolderOpen,
  PalmtreeIcon,
  MessageCircle,
  Star,
  Edit,
  ChevronDown,
  ChevronLeft,
  LogOut,
  Menu,
  X,
  Receipt,
  Home,
  Truck,
  Scale,
  Compass,
  Flag,
  Clock,
  BarChart3,
  LayoutGrid,
  Play,
  Pause,
  Square,
  Coffee,
} from 'lucide-react';
import { timeClockAPI, timeTrackingAPI, teamAPI } from '../lib/api';
import { toast } from 'sonner';
import { ImpersonationBanner } from './ImpersonationBanner';
import { useInboxAccess } from '../hooks/useInboxAccess';
import { META_DIRECT_ENABLED } from '../lib/features';
import { FloatingTimer } from './FloatingTimer';

const roleConfig = {
  ceo: {
    label: 'CEO',
    color: 'bg-emerald-500',
    defaultPath: '/ceo-dashboard',
  },
  manager: {
    label: 'Manager',
    color: 'bg-blue-500',
    defaultPath: '/dashboard',
  },
  member: {
    label: 'Miembro',
    color: 'bg-amber-500',
    defaultPath: '/member-dashboard',
  },
  client: {
    label: 'Cliente',
    color: 'bg-purple-500',
    defaultPath: '/client-portal',
  },
  collaborator: {
    label: 'Colaborador externo',
    color: 'bg-slate-500',
    defaultPath: '/collab',
  },
};

const menuConfig = {
  ceo: [
    {
      section: 'Dirección',
      items: [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/ceo-dashboard' },
        { icon: Calendar, label: 'Mi Calendario', path: '/ceo-calendar' },
        { icon: Flag, label: 'Objetivos', path: '/objectives' },
        { icon: Target, label: 'CRM', path: '/crm' },
        { icon: CheckSquare, label: 'Mis Tareas', path: '/tasks' },
      ],
    },
    {
      section: 'Departamentos',
      items: [
        { icon: DollarSign, label: 'Finanzas', path: '/finance-dashboard' },
        { icon: Megaphone, label: 'Marketing', path: '/marketing' },
        { icon: MessageSquare, label: 'Redes Sociales', path: '/social-media' },
        { icon: TrendingUp, label: 'Ventas', path: '/sales' },
        { icon: Settings2, label: 'Operaciones', path: '/operations' },
        { icon: Truck, label: 'Entrega de Servicios', path: '/service-delivery' },
        { icon: GraduationCap, label: 'Aprendizajes Ads', path: '/learnings' },
        { icon: LayoutGrid, label: 'Publicaciones', path: '/publications' },
        { icon: MessageSquare, label: 'Bandeja social', path: '/social-inbox' },
        { icon: Target, label: 'Producto', path: '/product' },
        { icon: Scale, label: 'Legal', path: '/legal' },
        { icon: Users, label: 'Equipo', path: '/team' },
        { icon: FolderOpen, label: 'Recursos', path: '/resources' },
        { icon: PalmtreeIcon, label: 'Ausencias', path: '/time-off' },
        { icon: Edit, label: 'Feedback', path: '/feedback' },
        { icon: Heart, label: 'Mindset', path: '/mindset' },
      ],
    },
    {
      section: 'Configuración',
      items: [
        { icon: Users, label: 'Usuarios y Roles', path: '/settings/users' },
        { icon: Settings2, label: 'Integraciones', path: '/integrations' },
      ],
    },
  ],
  manager: [
    {
      section: 'Gestión',
      items: [
        { icon: LayoutDashboard, label: 'Mi Dashboard', path: '/dashboard' },
        { icon: DollarSign, label: 'Finanzas', path: '/finance-dashboard' },
        { icon: Users, label: 'Mi Equipo', path: '/team' },
      ],
    },
    {
      section: 'Portal de Equipo',
      items: [
        { icon: Bell, label: 'Notificaciones', path: '/notifications' },
        { icon: Target, label: 'CRM', path: '/crm' },
        { icon: Calendar, label: 'Mi Calendario', path: '/ceo-calendar' },
        { icon: Users, label: 'Equipo', path: '/team-directory' },
        { icon: CheckSquare, label: 'Mis Tareas', path: '/tasks' },
        { icon: FolderOpen, label: 'Recursos', path: '/resources' },
        { icon: PalmtreeIcon, label: 'Ausencias', path: '/time-off' },
        { icon: MessageCircle, label: 'Foro', path: '/forum' },
        { icon: Star, label: 'Reconocimientos', path: '/recognition' },
        { icon: Edit, label: 'Feedback', path: '/feedback' },
      ],
    },
  ],
  member: [
    {
      section: 'Mi Espacio',
      items: [
        { icon: Home, label: 'Mi Dashboard', path: '/member-dashboard' },
        { icon: CheckSquare, label: 'Mis Tareas', path: '/tasks' },
        { icon: Calendar, label: 'Calendario Contenido', path: '/content-calendar' },
        { icon: LayoutGrid, label: 'Publicaciones', path: '/publications' },
      ],
    },
    {
      section: 'Portal de Equipo',
      items: [
        { icon: Bell, label: 'Notificaciones', path: '/notifications' },
        { icon: Calendar, label: 'Eventos', path: '/events' },
        { icon: FolderOpen, label: 'Recursos', path: '/resources' },
        { icon: PalmtreeIcon, label: 'Ausencias', path: '/time-off' },
        { icon: Edit, label: 'Feedback', path: '/feedback' },
        { icon: UserCircle, label: 'Mi Perfil', path: '/profile' },
      ],
    },
  ],
  client: [
    {
      section: 'Mi Portal',
      items: [
        { icon: Heart, label: 'Bienvenida', path: '/client-portal/welcome' },
        { icon: Home, label: 'Mi Portal', path: '/client-portal' },
        { icon: CheckSquare, label: 'Aprobaciones', path: '/client-portal/approvals' },
        { icon: FileText, label: 'Briefing', path: '/client-portal/briefing' },
        { icon: Calendar, label: 'Calendario', path: '/client-portal/calendar' },
        { icon: BookOpen, label: 'Mis Tareas', path: '/client-portal/tasks' },
        { icon: FolderOpen, label: 'Documentos', path: '/client-portal/documents' },
        { icon: Receipt, label: 'Facturas', path: '/client-portal/invoices' },
        { icon: BarChart3, label: 'Informes', path: '/client-portal/reports' },
        { icon: Megaphone, label: 'Campañas', path: '/client-portal/campaigns' },
      ],
    },
  ],
  collaborator: [
    {
      section: 'Grabaciones',
      items: [
        { icon: Calendar, label: 'Contenido a grabar', path: '/collab' },
      ],
    },
  ],
};

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, viewAsRole, setViewAsRole, logout, impersonate, impersonation } = useAuthStore();
  const inboxAccess = useInboxAccess(user);
  const { sidebarCollapsed, toggleSidebar } = useUIStore();
  React.useEffect(() => {
    if (window.innerWidth < 768 && !useUIStore.getState().sidebarCollapsed) toggleSidebar();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [viewAsMembers, setViewAsMembers] = React.useState([]);
  const loadViewAsMembers = async () => {
    try {
      const list = await teamAPI.getAll();
      setViewAsMembers((Array.isArray(list) ? list : []).filter((m) => m.role !== 'ceo' && m.is_active !== false));
    } catch (e) { /* silencioso */ }
  };
  const handleImpersonate = async (m) => {
    try {
      const u = await impersonate(m.id);
      toast.success(`Ahora ves el portal como ${u.name}`);
      navigate(roleConfig[u.role]?.defaultPath || '/member-dashboard');
    } catch (e) {
      toast.error(e.message);
    }
  };
  const { unreadCount, fetchNotifications, notifications } = useNotificationStore();

  const seenUnreadRef = React.useRef(null);
  React.useEffect(() => {
    fetchNotifications();
    const id = setInterval(() => fetchNotifications(), 20000);
    return () => clearInterval(id);
  }, [fetchNotifications]);

  // Ping suave cuando llega una notificación NUEVA sin leer (no en la primera carga)
  React.useEffect(() => {
    const unreadIds = new Set((notifications || []).filter((n) => !n.read).map((n) => n.id));
    if (seenUnreadRef.current === null) {
      seenUnreadRef.current = unreadIds;
      return;
    }
    let hasNew = false;
    unreadIds.forEach((idv) => { if (!seenUnreadRef.current.has(idv)) hasNew = true; });
    if (hasNew) playPing();
    seenUnreadRef.current = unreadIds;
  }, [notifications]);

  const currentRole = viewAsRole || user?.role || 'member';
  const inboxOk = META_DIRECT_ENABLED && inboxAccess === true;
  const menu = (menuConfig[currentRole] || menuConfig.member).map((sec) => ({ ...sec, items: sec.items.filter((it) => it.path !== '/social-inbox' || inboxOk) }));
  const config = roleConfig[currentRole];

  const canSwitchRoles = user?.role === 'ceo' && !impersonation;

  return (
    <TooltipProvider>
      <motion.aside
        initial={false}
        animate={{ width: sidebarCollapsed ? 80 : 280 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="fixed left-0 top-0 h-screen bg-[#e9e1d4] border-r border-border/40 z-50 flex flex-col"
        data-testid="sidebar"
      >
        {/* Header */}
        <div className="p-4 flex items-center justify-between border-b border-border/40">
          <AnimatePresence mode="wait">
            {!sidebarCollapsed && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-2"
              >
                <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
                  <span className="text-white font-bold text-sm">K</span>
                </div>
                <span className="font-bold text-lg tracking-tight">Karma Digital</span>
              </motion.div>
            )}
          </AnimatePresence>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="shrink-0"
            data-testid="toggle-sidebar-btn"
          >
            {sidebarCollapsed ? <Menu className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </Button>
        </div>

        {/* Role Selector */}
        {canSwitchRoles && (
          <div className="p-4 border-b border-border/40">
            <DropdownMenu onOpenChange={(o) => { if (o && viewAsMembers.length === 0) loadViewAsMembers(); }}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className={`w-full justify-between ${sidebarCollapsed ? 'px-2' : ''}`}
                  data-testid="role-selector"
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${config.color}`} />
                    {!sidebarCollapsed && <span>{config.label}</span>}
                  </div>
                  {!sidebarCollapsed && <ChevronDown className="w-4 h-4" />}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuLabel>Ver como</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {Object.entries(roleConfig).filter(([role]) => role !== 'collaborator').map(([role, cfg]) => (
                  <DropdownMenuItem
                    key={role}
                    onClick={() => {
                      setViewAsRole(role);
                      navigate(cfg.defaultPath);
                    }}
                    data-testid={`role-option-${role}`}
                  >
                    <div className={`w-2 h-2 rounded-full ${cfg.color} mr-2`} />
                    {cfg.label}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">Ver el portal como…</DropdownMenuLabel>
                {viewAsMembers.length === 0 && (
                  <DropdownMenuItem disabled className="text-xs text-muted-foreground">Cargando equipo…</DropdownMenuItem>
                )}
                {viewAsMembers.map((m) => (
                  <DropdownMenuItem key={m.id} onClick={() => handleImpersonate(m)} data-testid={`view-as-user-${m.id}`}>
                    <Avatar className="w-5 h-5 mr-2"><AvatarFallback className="text-[9px]">{m.avatar || (m.name || '?').slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
                    <span className="flex-1 truncate">{m.name}</span>
                    {m.profile?.position && <span className="text-[10px] text-muted-foreground ml-2 truncate max-w-[80px]">{m.profile.position}</span>}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="px-2 space-y-6">
            {menu.map((section, idx) => (
              <div key={idx}>
                {!sidebarCollapsed && (
                  <h3 className="px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {section.section}
                  </h3>
                )}
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const isActive = location.pathname === item.path;
                    const Icon = item.icon;
                    
                    const linkContent = (
                      <Link
                        to={item.path}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                          isActive
                            ? 'sidebar-active bg-primary/10 text-primary font-medium'
                            : 'text-foreground/70 hover:bg-white/50 hover:text-foreground'
                        } ${sidebarCollapsed ? 'justify-center' : ''}`}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <Icon className={`w-5 h-5 shrink-0 ${isActive ? 'text-primary' : ''}`} />
                        {!sidebarCollapsed && (
                          <span className="truncate">{item.label}</span>
                        )}
                        {!sidebarCollapsed && item.badge && (
                          <Badge variant="secondary" className="ml-auto text-xs">
                            {item.badge}
                          </Badge>
                        )}
                        {!sidebarCollapsed && item.path === '/notifications' && unreadCount > 0 && (
                          <Badge className="ml-auto bg-accent text-accent-foreground">
                            {unreadCount}
                          </Badge>
                        )}
                      </Link>
                    );

                    if (sidebarCollapsed) {
                      return (
                        <Tooltip key={item.path} delayDuration={0}>
                          <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                          <TooltipContent side="right">
                            <p>{item.label}</p>
                          </TooltipContent>
                        </Tooltip>
                      );
                    }

                    return <div key={item.path}>{linkContent}</div>;
                  })}
                </div>
              </div>
            ))}
          </nav>
        </ScrollArea>

        {/* User Profile */}
        <div className="p-4 border-t border-border/40">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className={`w-full justify-start gap-3 h-auto py-2 ${sidebarCollapsed ? 'px-2 justify-center' : ''}`}
                data-testid="user-menu"
              >
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.avatar} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                    {user?.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                {!sidebarCollapsed && (
                  <div className="text-left">
                    <p className="text-sm font-medium truncate">{user?.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Mi Cuenta</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/profile')}>
                <UserCircle className="w-4 h-4 mr-2" />
                Mi Perfil
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/settings')}>
                <Settings2 className="w-4 h-4 mr-2" />
                Configuración
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Cerrar Sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </motion.aside>
    </TooltipProvider>
  );
}

function fmtClockCompact(secs) {
  const s = Math.max(0, Math.floor(secs || 0));
  const h = String(Math.floor(s / 3600)).padStart(2, '0');
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  return `${h}:${m}`;
}

function liveSecs(session) {
  if (!session) return 0;
  const start = new Date(session.started_at).getTime();
  const now = Date.now();
  let paused = 0;
  (session.pauses || []).forEach((p) => {
    const ps = new Date(p.start).getTime();
    const pe = p.end ? new Date(p.end).getTime() : now;
    if (pe > ps) paused += pe - ps;
  });
  return Math.max(0, Math.floor((now - start - paused) / 1000));
}

// Fichaje rápido en la barra superior (solo para miembros con método 'jornada')
function QuickClock() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isInternal = user && !['client', 'collaborator'].includes(user.role);
  const canSeeTimeTotals = user && ['ceo', 'manager'].includes(user.role); // miembros: solo iniciar/parar, sin totales
  const [method, setMethod] = React.useState(null);
  const [active, setActive] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [, setTick] = React.useState(0);

  const refresh = React.useCallback(async () => {
    try {
      const a = await timeClockAPI.getActive();
      setActive(a.session);
    } catch (e) { /* noop */ }
  }, []);

  React.useEffect(() => {
    if (!isInternal) return;
    let mounted = true;
    timeTrackingAPI.getConfig()
      .then((c) => { if (mounted) { setMethod(c.method || 'task'); if (c.method === 'jornada') refresh(); } })
      .catch(() => { if (mounted) setMethod('task'); });
    return () => { mounted = false; };
  }, [refresh, isInternal]);

  React.useEffect(() => {
    if (method !== 'jornada') return;
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, [method, refresh]);

  React.useEffect(() => {
    if (!active || active.status === 'ended') return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [active]);

  if (!isInternal || method !== 'jornada') return null;

  const doAction = async (fn) => {
    setBusy(true);
    try { await fn(); await refresh(); }
    catch (e) { /* noop */ }
    setBusy(false);
  };

  if (!active) {
    return (
      <Button size="sm" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 h-9" onClick={() => doAction(timeClockAPI.start)} data-testid="topbar-clock-start">
        <Play className="w-4 h-4 mr-1.5" /> Fichar
      </Button>
    );
  }

  const paused = active.status === 'paused';
  return (
    <div className="flex items-center gap-1.5 rounded-full border bg-muted/40 pl-3 pr-1.5 py-1" data-testid="topbar-clock-widget">
      <button className="flex items-center gap-1.5" onClick={() => { if (canSeeTimeTotals) navigate('/time-tracking'); }} title={canSeeTimeTotals ? 'Abrir Registro de Tiempo' : (paused ? 'Jornada en pausa' : 'Jornada en curso')}>
        <span className={`w-2 h-2 rounded-full ${paused ? 'bg-amber-500' : 'bg-emerald-500 animate-pulse'}`} />
        {canSeeTimeTotals ? (
          <span className="text-sm font-semibold tabular-nums" data-testid="topbar-clock-time">{fmtClockCompact(liveSecs(active))}</span>
        ) : (
          <span className="text-xs font-medium" data-testid="topbar-clock-status">{paused ? 'En pausa' : 'Fichado'}</span>
        )}
      </button>
      {paused ? (
        <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600" disabled={busy} onClick={() => doAction(timeClockAPI.resume)} data-testid="topbar-clock-resume" title="Reanudar"><Play className="w-4 h-4" /></Button>
      ) : (
        <Button size="icon" variant="ghost" className="h-7 w-7 text-amber-600" disabled={busy} onClick={() => doAction(timeClockAPI.pause)} data-testid="topbar-clock-pause" title="Pausa"><Coffee className="w-4 h-4" /></Button>
      )}
      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" disabled={busy} onClick={() => doAction(timeClockAPI.stop)} data-testid="topbar-clock-stop" title="Fichar salida"><Square className="w-4 h-4" /></Button>
    </div>
  );
}

export function TopBar() {
  const { user } = useAuthStore();
  const navigate = useNavigate();

  return (
    <header className="h-16 bg-white/80 backdrop-blur-md border-b border-border/40 flex items-center justify-between px-6" data-testid="topbar">
      <div>
        <h1 className="text-lg font-semibold">{user?.organization_name || 'Portal Karma Digital'}</h1>
      </div>
      <div className="flex items-center gap-3">
        <QuickClock />
        <NotificationsDropdown />
      </div>
    </header>
  );
}

export function Layout({ children }) {
  const { sidebarCollapsed } = useUIStore();
  const layoutUser = useAuthStore((s) => s.user);
  const showTimer = layoutUser && !['client', 'collaborator'].includes(layoutUser.role);

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <div
        className="transition-all duration-300"
        style={{ marginLeft: sidebarCollapsed ? 80 : 280 }}
      >
        <ImpersonationBanner />
        <TopBar />
        <main className="p-6 pb-24 min-h-[calc(100vh-64px)]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          >
            {children}
          </motion.div>
        </main>
      </div>
      {showTimer && <FloatingTimer />}
    </div>
  );
}
